import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { usePracticeMatchConnection, PracticeDifficulty } from '../../state/practiceMatchStore';
import { usePracticeStatsStore } from '../../state/practiceStatsStore';
import { PracticeShopPanel } from '../components/practice/PracticeShopPanel';
import { useGameEffectsCleanup } from '../../hooks/useGameEffectsCleanup';
import { soundManager } from '../../utils/sounds';
import { DeckCard, TurnResolution, JokerDefinition, ConsumableDefinition } from '@solante/shared';

const PLAYER_ID = 'player';
const AI_ID = 'ai';

// Suit emoji mapping
const suitEmoji: Record<string, string> = {
  Stake: '★',
  Sol: '☀',
  Burn: '✦',
  Mint: '≋',
};

// Turn announcement component
const TurnAnnouncement: React.FC<{
  type: 'your_turn' | 'ai_turn' | 'shop_phase';
  show: boolean;
  onComplete: () => void;
}> = ({ type, show, onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (show) {
      if (type === 'ai_turn') return;
      const duration = type === 'your_turn' ? 500 : 1500;
      const timer = setTimeout(() => onCompleteRef.current(), duration);
      return () => clearTimeout(timer);
    }
  }, [show, type]);

  if (!show) return null;

  const config = {
    your_turn: {
      text: 'YOUR TURN',
      color: 'text-arcade-cyan',
      glow: 'drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]',
    },
    ai_turn: {
      text: 'AI IS THINKING...',
      color: 'text-amber-400',
      glow: 'drop-shadow-[0_0_15px_rgba(255,180,0,0.6)]',
    },
    shop_phase: {
      text: 'SHOP IS OPEN',
      color: 'text-emerald-400',
      glow: 'drop-shadow-[0_0_15px_rgba(0,255,150,0.6)]',
    },
  }[type];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 2.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
    >
      <motion.div
        initial={{ y: -30 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        className={`font-display text-2xl font-bold sm:text-4xl md:text-5xl ${config.color} ${config.glow}`}
      >
        {config.text}
      </motion.div>
    </motion.div>
  );
};

// Simple card display component for practice mode
const PracticeCard: React.FC<{
  card: DeckCard;
  isSelected: boolean;
  onToggle: () => void;
  disabled: boolean;
}> = ({ card, isSelected, onToggle, disabled }) => {
  const suit = card.suit;
  const emoji = suitEmoji[suit] || '🃏';

  return (
    <motion.button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      whileHover={{ y: disabled ? 0 : -8 }}
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      className={`relative w-16 h-24 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
        isSelected
          ? 'border-arcade-cyan bg-arcade-cyan/20 -translate-y-2'
          : 'border-white/20 bg-white/5 hover:border-white/40'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span className="text-2xl">{emoji}</span>
      <span className="text-sm font-bold text-white">{card.rank}</span>
      <span className="text-xs text-white/60">{card.baseValue}</span>
    </motion.button>
  );
};

// Simple hand selector for practice mode
const PracticeHandSelector: React.FC<{
  cards: DeckCard[];
  selectedCards: string[];
  onSelectCards: (ids: string[]) => void;
  onPlayHand: (ids: string[]) => Promise<void>;
  disabled: boolean;
}> = ({ cards, selectedCards, onSelectCards, onPlayHand, disabled }) => {
  const [submitting, setSubmitting] = useState(false);

  const toggleCard = (cardId: string) => {
    if (disabled) return;
    if (selectedCards.includes(cardId)) {
      onSelectCards(selectedCards.filter((id) => id !== cardId));
    } else if (selectedCards.length < 5) {
      onSelectCards([...selectedCards, cardId]);
    }
  };

  const handlePlay = async () => {
    if (selectedCards.length < 1 || submitting) return;
    setSubmitting(true);
    try {
      await onPlayHand(selectedCards);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-center gap-2">
        {cards.map((card) => (
          <PracticeCard
            key={card.id}
            card={card}
            isSelected={selectedCards.includes(card.id)}
            onToggle={() => toggleCard(card.id)}
            disabled={disabled}
          />
        ))}
      </div>
      <div className="text-center">
        <button
          type="button"
          onClick={handlePlay}
          disabled={selectedCards.length < 1 || disabled || submitting}
          className={`px-8 py-3 rounded-xl font-semibold text-lg transition-all ${
            selectedCards.length >= 1 && !disabled && !submitting
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500'
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
        >
          {submitting
            ? 'Playing...'
            : `Play ${selectedCards.length} card${selectedCards.length !== 1 ? 's' : ''}`}
        </button>
        <p className="text-xs text-white/40 mt-2">Select 1-5 cards to play</p>
      </div>
    </div>
  );
};

// Simple run state display
const PracticeRunState: React.FC<{
  label: string;
  chips: number;
  hearts: number;
  totalScore: number;
  targetScore: number;
  jokers: JokerDefinition[];
}> = ({ label, chips, hearts, totalScore, targetScore, jokers }) => {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-white/60 text-center">{label}</h3>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-lg font-bold text-yellow-400">{chips}</div>
          <div className="text-xs text-white/40">Chips</div>
        </div>
        <div>
          <div className="text-lg font-bold text-red-400">{'❤️'.repeat(Math.max(0, hearts))}</div>
          <div className="text-xs text-white/40">Hearts</div>
        </div>
        <div>
          <div className="text-lg font-bold text-arcade-cyan">{totalScore}</div>
          <div className="text-xs text-white/40">/{targetScore}</div>
        </div>
      </div>
      {jokers.length > 0 && (
        <div className="pt-2 border-t border-white/10">
          <div className="text-xs text-white/40 mb-1">Jokers:</div>
          <div className="flex flex-wrap gap-1">
            {jokers.map((j, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 bg-purple-500/20 rounded text-purple-300"
              >
                {j.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Simple turn history display
const PracticeTurnHistory: React.FC<{
  turns: TurnResolution[];
}> = ({ turns }) => {
  if (turns.length === 0) {
    return <div className="text-sm text-white/40 text-center p-4">No turns played yet</div>;
  }

  return (
    <div className="space-y-2 p-2 max-h-40 overflow-y-auto">
      {turns
        .slice()
        .reverse()
        .slice(0, 10)
        .map((turn, i) => (
          <div
            key={turn.sequence}
            className="flex items-center justify-between text-sm p-2 rounded bg-white/5"
          >
            <span className="text-white/60">
              {turn.player === PLAYER_ID ? 'You' : 'AI'} - Turn {turn.sequence + 1}
            </span>
            <span className="font-bold text-arcade-cyan">{turn.breakdown.total}</span>
          </div>
        ))}
    </div>
  );
};

// Match result modal
const MatchResultModal: React.FC<{
  winner: string;
  reason: string;
  playerScore: number;
  aiScore: number;
  difficulty: PracticeDifficulty;
  onPlayAgain: () => void;
  onExit: () => void;
}> = ({ winner, reason, playerScore, aiScore, difficulty, onPlayAgain, onExit }) => {
  const playerWon = winner === PLAYER_ID;
  const { recordResult } = usePracticeStatsStore();

  useEffect(() => {
    recordResult(playerWon, difficulty, playerScore);
    soundManager.play(playerWon ? 'win' : 'lose');
  }, [playerWon, difficulty, playerScore, recordResult]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="arcade-panel p-4 text-center space-y-4 max-w-md w-full sm:p-8 sm:space-y-6"
      >
        <div className="space-y-2">
          <div
            className={`mx-auto w-20 h-20 rounded-full flex items-center justify-center text-4xl ${
              playerWon
                ? 'bg-green-500/20 border-2 border-green-500/50'
                : 'bg-red-500/20 border-2 border-red-500/50'
            }`}
          >
            {playerWon ? '🏆' : '💔'}
          </div>
          <h2
            className={`font-display text-3xl font-bold ${
              playerWon ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {playerWon ? 'VICTORY!' : 'DEFEAT'}
          </h2>
          <p className="text-sm text-white/60">
            {reason === 'score'
              ? 'Target score reached'
              : reason === 'hearts'
                ? 'Hearts depleted'
                : 'Match forfeited'}
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 sm:gap-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">YOU</div>
            <div className="font-display text-3xl font-bold text-arcade-cyan sm:text-4xl">
              {playerScore}
            </div>
          </div>
          <div className="font-display text-xl font-bold text-white/30 sm:text-2xl">VS</div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">AI</div>
            <div className="font-display text-3xl font-bold text-red-400 sm:text-4xl">
              {aiScore}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onPlayAgain}
            className="flex-1 arcade-btn bg-gradient-to-r from-purple-600 to-pink-600"
          >
            Play Again
          </button>
          <button
            type="button"
            onClick={onExit}
            className="flex-1 arcade-btn bg-white/10 hover:bg-white/20"
          >
            Exit
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export const PracticeMatchPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    state,
    connecting,
    signalReady,
    playTurn,
    purchaseOffer,
    rerollShop,
    signalShopReady,
    forfeit,
    disconnect,
  } = usePracticeMatchConnection();

  // Cleanup game effects on unmount
  useGameEffectsCleanup();

  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showTurnAnnouncement, setShowTurnAnnouncement] = useState(false);
  const [turnAnnouncementType, setTurnAnnouncementType] = useState<
    'your_turn' | 'ai_turn' | 'shop_phase'
  >('your_turn');
  const [showShopAnnouncement, setShowShopAnnouncement] = useState(false);
  const [purchasedOfferIds, setPurchasedOfferIds] = useState<Set<string>>(new Set());
  const prevTurnRef = useRef<string | undefined>();
  const prevPhaseRef = useRef<string | undefined>();

  // Derived state
  const status = state?.status ?? 'waiting';
  const phase = state?.phase ?? 'play';
  const currentTurnId = state?.currentTurnId ?? '';
  const isMyTurn = currentTurnId === PLAYER_ID;
  const isInProgress = status === 'inProgress';
  const isCompleted = status === 'completed';
  const isShopPhase = phase === 'shop';

  const playerRunState = state?.playerRunStates?.[PLAYER_ID];
  const aiRunState = state?.playerRunStates?.[AI_ID];
  const playerShopState = state?.playerShopStates?.[PLAYER_ID];

  const targetScore = state?.targetScore ?? 1000;
  const playerScore = playerRunState?.totalScore ?? 0;
  const aiScore = aiRunState?.totalScore ?? 0;

  // Turn timer
  useEffect(() => {
    const deadlineIso = state?.turnDeadlineIso;
    if (!deadlineIso) {
      setTimeLeft(null);
      return;
    }
    const deadlineMs = Date.parse(deadlineIso);
    if (Number.isNaN(deadlineMs)) {
      setTimeLeft(null);
      return;
    }
    const update = () => {
      const diff = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
      setTimeLeft(diff);
    };
    update();
    const interval = window.setInterval(update, 250);
    return () => window.clearInterval(interval);
  }, [state?.turnDeadlineIso]);

  // Turn change announcements
  useEffect(() => {
    if (prevTurnRef.current !== currentTurnId && isInProgress && phase === 'play') {
      prevTurnRef.current = currentTurnId;
      setTurnAnnouncementType(isMyTurn ? 'your_turn' : 'ai_turn');
      setShowTurnAnnouncement(true);
    }
  }, [currentTurnId, isMyTurn, isInProgress, phase]);

  // Shop phase announcement
  useEffect(() => {
    if (prevPhaseRef.current !== phase && phase === 'shop' && isInProgress) {
      prevPhaseRef.current = phase;
      setTurnAnnouncementType('shop_phase');
      setShowShopAnnouncement(true);
      setPurchasedOfferIds(new Set()); // Reset purchases for new shop
    } else {
      prevPhaseRef.current = phase;
    }
  }, [phase, isInProgress]);

  // Clear selected cards when turn changes
  useEffect(() => {
    if (!isMyTurn) {
      setSelectedCards([]);
    }
  }, [isMyTurn]);

  const handlePlayHand = useCallback(
    async (cards: string[]) => {
      try {
        await playTurn({ playedCardIds: cards });
        soundManager.play('select');
        setSelectedCards([]);
      } catch (err) {
        toast.error((err as Error).message);
        soundManager.play('error');
      }
    },
    [playTurn],
  );

  const handleSignalReady = useCallback(async () => {
    try {
      await signalReady();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }, [signalReady]);

  const handlePurchase = useCallback(
    async (offerId: string) => {
      try {
        await purchaseOffer(offerId);
        setPurchasedOfferIds((prev) => new Set([...prev, offerId]));
        toast.success('Purchased!');
      } catch (err) {
        toast.error((err as Error).message);
      }
    },
    [purchaseOffer],
  );

  const handlePlayAgain = useCallback(() => {
    disconnect();
    navigate('/practice');
  }, [disconnect, navigate]);

  const handleExit = useCallback(() => {
    disconnect();
    navigate('/');
  }, [disconnect, navigate]);

  const handleForfeit = useCallback(async () => {
    try {
      await forfeit();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }, [forfeit]);

  // Waiting state
  if (status === 'waiting') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="arcade-panel p-4 text-center space-y-4 max-w-md w-full sm:p-8 sm:space-y-6"
        >
          <div className="space-y-2">
            <div className="mx-auto w-16 h-16 rounded-full border-4 border-purple-500/30 flex items-center justify-center text-3xl">
              🎮
            </div>
            <h2 className="font-display text-2xl font-bold text-purple-300">PRACTICE MODE</h2>
            <p className="text-sm text-white/60">
              Difficulty:{' '}
              <span className="capitalize text-white">{state?.difficulty ?? 'Medium'}</span>
            </p>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Match ID</p>
            <p className="font-mono text-neon-300">#{state?.matchId?.slice(0, 8) ?? '...'}</p>
          </div>

          <button
            type="button"
            onClick={handleSignalReady}
            className="arcade-btn w-full bg-gradient-to-r from-purple-600 to-pink-600"
          >
            Start Match
          </button>

          <button
            type="button"
            onClick={handleExit}
            className="text-sm text-white/40 hover:text-white/60"
          >
            Cancel
          </button>
        </motion.div>
      </div>
    );
  }

  // Loading state
  if (connecting || !state) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 pt-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="arcade-panel p-8 text-center space-y-4"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="mx-auto w-12 h-12 rounded-full border-4 border-purple-500/30 border-t-purple-400"
          />
          <p className="text-white/60">Connecting to practice room...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden pt-14">
      {/* Match Result Modal */}
      {isCompleted && state.winner && (
        <MatchResultModal
          winner={state.winner}
          reason={state.winReason ?? 'score'}
          playerScore={playerScore}
          aiScore={aiScore}
          difficulty={state.difficulty}
          onPlayAgain={handlePlayAgain}
          onExit={handleExit}
        />
      )}

      {/* Turn Announcements */}
      <AnimatePresence>
        {showTurnAnnouncement && (
          <TurnAnnouncement
            type={turnAnnouncementType}
            show={showTurnAnnouncement}
            onComplete={() => setShowTurnAnnouncement(false)}
          />
        )}
        {showShopAnnouncement && (
          <TurnAnnouncement
            type="shop_phase"
            show={showShopAnnouncement}
            onComplete={() => setShowShopAnnouncement(false)}
          />
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left sidebar - Run state */}
        <div className="w-64 border-r border-white/10 bg-black/40 overflow-y-auto p-4 hidden md:block">
          <div className="space-y-4">
            <PracticeRunState
              label="YOU"
              chips={playerRunState?.chips ?? 320}
              hearts={playerRunState?.hearts ?? 3}
              totalScore={playerScore}
              targetScore={targetScore}
              jokers={playerRunState?.jokers ?? []}
            />

            <div className="border-t border-white/10 pt-4">
              <PracticeRunState
                label={`AI (${state.difficulty.toUpperCase()})`}
                chips={aiRunState?.chips ?? 320}
                hearts={aiRunState?.hearts ?? 3}
                totalScore={aiScore}
                targetScore={targetScore}
                jokers={aiRunState?.jokers ?? []}
              />
            </div>

            <div className="border-t border-white/10 pt-4 text-center">
              <div className="text-xs text-white/40 mb-1">TURN</div>
              <div className="font-display text-lg text-white">
                {state.timeline.length + 1} / 25
              </div>
            </div>
          </div>

          {isInProgress && (
            <button
              type="button"
              onClick={handleForfeit}
              className="mt-4 w-full py-2 text-sm text-red-400 hover:text-red-300 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors"
            >
              Forfeit Match
            </button>
          )}
        </div>

        {/* Center - Game area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Score bar */}
          <div className="bg-black/60 border-b border-white/10 px-4 py-2">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-xs text-white/40">YOU</div>
                <div className="font-display text-xl text-arcade-cyan">{playerScore}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-white/40">TARGET</div>
                <div className="font-display text-lg text-white/60">{targetScore}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-white/40">AI</div>
                <div className="font-display text-xl text-red-400">{aiScore}</div>
              </div>
            </div>
          </div>

          {/* Turn timer */}
          {timeLeft !== null && isMyTurn && !isShopPhase && (
            <div className="bg-black/40 px-4 py-2 text-center">
              <div className="text-sm text-white/60">
                Time remaining:{' '}
                <span className={timeLeft <= 10 ? 'text-red-400' : 'text-white'}>{timeLeft}s</span>
              </div>
            </div>
          )}

          {/* Shop phase */}
          {isShopPhase && playerShopState && (
            <div className="flex-1 overflow-y-auto p-4">
              <PracticeShopPanel
                shop={playerShopState}
                chips={playerRunState?.chips ?? 0}
                jokerSlots={playerRunState?.jokerSlots ?? 3}
                currentJokerCount={playerRunState?.jokers?.length ?? 0}
                purchasedOfferIds={purchasedOfferIds}
                onPurchase={handlePurchase}
                onReroll={rerollShop}
                onReady={signalShopReady}
              />
            </div>
          )}

          {/* Play phase - Hand selector */}
          {!isShopPhase && isInProgress && (
            <div className="flex-1 flex flex-col items-center justify-center p-4">
              {isMyTurn && state.currentHand && state.currentHand.length > 0 ? (
                <PracticeHandSelector
                  cards={state.currentHand}
                  selectedCards={selectedCards}
                  onSelectCards={setSelectedCards}
                  onPlayHand={handlePlayHand}
                  disabled={!isMyTurn}
                />
              ) : (
                <div className="text-center">
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="text-lg text-amber-400"
                  >
                    AI is thinking...
                  </motion.div>
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="border-t border-white/10 bg-black/40">
            <PracticeTurnHistory turns={state.timeline} />
          </div>
        </div>

        {/* Mobile stats bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-black/80 border-t border-white/10 p-2">
          <div className="flex items-center justify-around text-center">
            <div>
              <div className="text-xs text-white/40">Hearts</div>
              <div className="text-lg text-red-400">{playerRunState?.hearts ?? 3}</div>
            </div>
            <div>
              <div className="text-xs text-white/40">Chips</div>
              <div className="text-lg text-yellow-400">{playerRunState?.chips ?? 0}</div>
            </div>
            <div>
              <div className="text-xs text-white/40">Score</div>
              <div className="text-lg text-arcade-cyan">{playerScore}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
